class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      const input = event.target;
      if (input.type !== 'checkbox') return;
      const group = Array.from(facetForm.querySelectorAll('input[type="checkbox"]')).filter(
        (twin) => twin.name === input.name && twin.value === input.value
      );
      const item = input.closest('li.manual-color');
      if (!item || !item.dataset.colorTooltip) {
        group.forEach((twin) => (twin.checked = input.checked));
        return;
      }

      const keys = group
        .map((twin) => twin.closest('li.manual-color'))
        .filter((li) => li && li.dataset.colorTooltip)
        .map(FacetFiltersForm.colorLabelKey);
      const key = FacetFiltersForm.colorLabelKey(item);
      const current = FacetFiltersForm.formColorLabels(facetForm);
      const wasChecked = !input.checked;
      let on = wasChecked ? keys.filter((k) => current.includes(k)) : [];
      if (wasChecked && on.length === 0) on = keys.slice();
      on = on.includes(key) ? on.filter((k) => k !== key) : on.concat(key);

      const others = current.filter((label) => !keys.includes(label));
      FacetFiltersForm.setFormColorLabels(facetForm, others.concat(on));
      group.forEach((twin) => (twin.checked = on.length > 0));
      FacetFiltersForm.paintColorLabels();
    });
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    FacetFiltersForm.syncColorLabels(window.location.search);
    FacetFiltersForm.paintColorChips();

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static colorLabelKey(li) {
    const input = li.querySelector('input[type="checkbox"]');
    return `${input.value.replace(/\s+/g, '').toLowerCase()}>>${li.dataset.colorTooltip}`;
  }

  static formColorLabels(form) {
    return Array.from(form.querySelectorAll('input[name="color_label"]')).map((hidden) => hidden.value);
  }

  static setFormColorLabels(form, labels) {
    form.querySelectorAll('input[name="color_label"]').forEach((hidden) => hidden.remove());
    labels.forEach((label) => {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'color_label';
      hidden.value = label;
      form.appendChild(hidden);
    });
  }

  static syncCheckboxes(searchParams) {
    const params = new URLSearchParams(searchParams);
    document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]').forEach((input) => {
      input.checked = params.getAll(input.name).includes(input.value);
      const label = input.closest('.facets__label');
      if (label) label.classList.toggle('active', input.checked);
    });
  }

  static syncColorLabels(searchParams) {
    const labels = new URLSearchParams(searchParams).getAll('color_label');
    document.querySelectorAll('#FacetFiltersForm, #FacetFiltersFormMobile').forEach((form) => {
      FacetFiltersForm.setFormColorLabels(form, labels);
    });
    FacetFiltersForm.paintColorLabels();
  }

  static paintColorLabels() {
    document.querySelectorAll('li.manual-color[data-color-tooltip]').forEach((li) => {
      const input = li.querySelector('input[type="checkbox"]');
      const label = li.querySelector('.facets__label');
      const form = li.closest('form');
      if (!input || !label || !form) return;
      const labels = FacetFiltersForm.formColorLabels(form);
      const key = FacetFiltersForm.colorLabelKey(li);
      const hexPrefix = `${key.split('>>')[0]}>>`;
      const hexNamed = labels.some((labelKey) => labelKey.startsWith(hexPrefix));
      label.classList.toggle('active', input.checked && (!hexNamed || labels.includes(key)));
    });
  }

  static selectableColorKeys(hexPrefix) {
    const desktop = document.getElementById('FacetFiltersForm');
    const form = desktop && desktop.offsetParent !== null ? desktop : document.getElementById('FacetFiltersFormMobile');
    if (!form) return [];
    return [
      ...new Set(
        Array.from(form.querySelectorAll('li.manual-color[data-color-tooltip]'))
          .map(FacetFiltersForm.colorLabelKey)
          .filter((labelKey) => labelKey.startsWith(hexPrefix))
      ),
    ];
  }

  static paintColorChips() {
    const urlLabels = new URLSearchParams(window.location.search).getAll('color_label');
    const chips = Array.from(document.querySelectorAll('facet-remove[data-color-key]'));
    chips.forEach((chip) => {
      const key = chip.dataset.colorKey;
      const hexPrefix = `${key.split('>>')[0]}>>`;
      const chipKeys = chips.map((other) => other.dataset.colorKey).filter((chipKey) => chipKey.startsWith(hexPrefix));
      const named = urlLabels.filter((labelKey) => chipKeys.includes(labelKey));
      const selectable = FacetFiltersForm.selectableColorKeys(hexPrefix).filter((labelKey) => chipKeys.includes(labelKey));
      const shown = named.length ? named : selectable;
      chip.hidden = shown.length > 0 && !shown.includes(key);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    FacetFiltersForm.syncCheckboxes(searchParams);
    FacetFiltersForm.syncColorLabels(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.coverFilterCounts();
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.coverFilterCounts();
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  // Cover the freshly server-rendered filter counts with the loading shimmer the
  // instant they are painted. The background loader only kicks in ~550ms later, so
  // without this the soon-to-be-recomputed server number flashes first and then
  // gets hidden — the count "blink". Keeps the shimmer; just removes the flash.
  static coverFilterCounts() {
    if (window.SacvinCollection && typeof window.SacvinCollection.setCountsLoading === 'function') {
      window.SacvinCollection.setCountsLoading(true);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // Re-initialize WishlistHero buttons immediately (before any DOM changes)
    FacetFiltersForm.reinitWishlistHero();

    // Notify card scripts after a delay so WishlistHero can finish initializing first
    setTimeout(function () {
      document.dispatchEvent(new CustomEvent('productGrid:ajaxUpdated', { bubbles: true }));
    }, 500);
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const parsedCount = parsedHTML.getElementById('ProductCount') || parsedHTML.getElementById('ProductCountDesktop');
    if (!parsedCount) return;
    const count = parsedCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentInDom = document.getElementById(elementToRender.parentElement.id);
          if (parentInDom) {
            // When all filters were previously stripped (e.g. a 0-results state),
            // there is no `.js-filter` left to anchor against — append instead of crashing.
            const anchor = parentInDom.querySelector('.js-filter');
            if (anchor) {
              anchor.before(elementToRender);
            } else {
              parentInDom.appendChild(elementToRender);
            }
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.paintColorLabels();
    FacetFiltersForm.paintColorChips();
    if (window.sacvinSyncPriceSort) window.sacvinSyncPriceSort();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) mobileForm.closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static reinitWishlistHero() {
    // WishlistHero documented approach for AJAX themes: dispatch this event after DOM update
    // Use plain Event (not CustomEvent) as per WishlistHero docs
    const triggerWLH = function () {
      document.dispatchEvent(new Event('wishlisthero:product:rendered'));
      if (window.WishlistHeroPublicApi && typeof window.WishlistHeroPublicApi.refresh === 'function') {
        window.WishlistHeroPublicApi.refresh();
      }
    };

    // Fire immediately after DOM replacement
    triggerWLH();
    // Fire again after 600ms to catch any late-loading cases
    setTimeout(triggerWLH, 600);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const priceInputs = form.querySelectorAll('price-range input');
    if (priceInputs.length === 2) {
      const minInput = priceInputs[0];
      const maxInput = priceInputs[1];
      const thumbMax = form.querySelector('.slider-wrapper .thumb-max');
      const absoluteMax = thumbMax ? parseFloat(thumbMax.getAttribute('max')) : null;
      const minVal = parseFloat(String(minInput.value).replace(/,/g, '')) || 0;
      const maxVal = parseFloat(String(maxInput.value).replace(/,/g, '')) || 0;
      if (minVal <= 0 && absoluteMax !== null && maxVal >= absoluteMax) {
        formData.delete(minInput.name);
        formData.delete(maxInput.name);
      }
    }
    const seen = new Set();
    const params = new URLSearchParams();
    for (const [name, value] of formData) {
      const pair = `${name}=${value}`;
      if (seen.has(pair)) continue;
      seen.add(pair);
      params.append(name, value);
    }
    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    const key = this.dataset.colorKey;
    if (!key) {
      form.onActiveFilterClick(event);
      return;
    }

    const hexPrefix = `${key.split('>>')[0]}>>`;
    const current = new URLSearchParams(window.location.search);
    const labels = current.getAll('color_label');
    const named = labels.filter((labelKey) => labelKey.startsWith(hexPrefix));
    const selectable = FacetFiltersForm.selectableColorKeys(hexPrefix);
    const allKeys = selectable.length
      ? selectable
      : [
          ...new Set(
            Array.from(document.querySelectorAll('facet-remove[data-color-key]'))
              .map((chip) => chip.dataset.colorKey)
              .filter((chipKey) => chipKey.startsWith(hexPrefix))
          ),
        ];
    const remaining = (named.length ? named : allKeys).filter((labelKey) => labelKey !== key);

    const href = this.querySelector('a').href;
    const params = remaining.length ? current : new URLSearchParams(href.includes('?') ? href.split('?')[1] : '');
    params.delete('color_label');
    params.delete('page');
    labels
      .filter((labelKey) => !labelKey.startsWith(hexPrefix))
      .concat(remaining)
      .forEach((labelKey) => params.append('color_label', labelKey));

    FacetFiltersForm.toggleActiveFacets();
    FacetFiltersForm.renderPage(params.toString());
  }
}

customElements.define('facet-remove', FacetRemove);
